import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { CheckCircle2, Circle, Lock, MapPin, AlertCircle, Loader2, Eye, EyeOff, Check, X } from "lucide-react";

const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }
];

interface SetupStatus {
  hasPassword: boolean;
  hasDefaultShippingAddress: boolean;
  isComplete: boolean;
}

interface DefaultAddress {
  id: string;
  locationName: string | null;
  line1: string;
  line2: string | null;
  city: string;
  region: string;
  postalCode: string;
  country: string;
  hasLoadingDock: boolean;
  hasForklift: boolean;
  requiresLiftgate: boolean;
}

interface SetupData {
  valid: boolean;
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  account: {
    id: string;
    companyName: string;
    isActive: boolean;
  } | null;
  setupStatus: SetupStatus;
  defaultShippingAddress: DefaultAddress | null;
}

export default function SetupAccountPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  
  const params = new URLSearchParams(window.location.search);
  const initialToken = params.get("token");
  
  // Store the active token (may be rotated after password set)
  const [activeToken, setActiveToken] = useState<string | null>(initialToken);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState<"password" | "address">("password");

  // Password validation
  const PASSWORD_REGEX = /^(?=.*[A-Z])(?=.*\d).{8,}$/;
  const passwordRequirements = [
    { text: 'At least 8 characters', met: password.length >= 8 },
    { text: 'At least one uppercase letter', met: /[A-Z]/.test(password) },
    { text: 'At least one number', met: /[0-9]/.test(password) },
  ];
  const allRequirementsMet = PASSWORD_REGEX.test(password);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const [addressForm, setAddressForm] = useState({
    locationName: "",
    line1: "",
    line2: "",
    city: "",
    region: "",
    postalCode: "",
    country: "US",
    deliveryContactName: "",
    deliveryContactPhone: "",
    deliveryContactEmail: "",
    hasLoadingDock: false,
    hasForklift: false,
    requiresLiftgate: true,
    isShipping: true,
    isBilling: false,
    isDefaultShipping: true,
    addressClassification: "WAREHOUSE" as const,
  });

  const { data: setupData, isLoading, error, refetch } = useQuery<SetupData>({
    queryKey: ["/api/setup-account/validate", activeToken],
    queryFn: async () => {
      const response = await fetch(`/api/setup-account/validate?token=${activeToken}`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to validate token");
      }
      return response.json();
    },
    enabled: !!activeToken,
  });

  useEffect(() => {
    if (setupData?.setupStatus) {
      if (setupData.setupStatus.hasPassword && !setupData.setupStatus.hasDefaultShippingAddress) {
        setCurrentStep("address");
      }
      if (setupData.setupStatus.isComplete) {
        setCurrentStep("address");
      }
    }
  }, [setupData]);

  const setPasswordMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/setup-account/set-password", { token: activeToken, password });
    },
    onSuccess: (data: { success: boolean; message: string; newToken?: string }) => {
      // Update to the new rotated token for subsequent steps
      if (data.newToken) {
        setActiveToken(data.newToken);
      }
      toast({
        title: "Password Set",
        description: "Your password has been set successfully.",
      });
      refetch();
      setCurrentStep("address");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const addAddressMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/setup-account/add-address", { token: activeToken, address: addressForm });
    },
    onSuccess: () => {
      toast({
        title: "Address Added",
        description: "Your shipping address has been added.",
      });
      refetch();
      completeSetupMutation.mutate();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const completeSetupMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/setup-account/complete", { token: activeToken });
    },
    onSuccess: () => {
      toast({
        title: "Account Activated!",
        description: "Your account is now active. You can log in and start shopping.",
      });
      setTimeout(() => navigate("/login"), 2000);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSetPassword = () => {
    if (!allRequirementsMet) {
      toast({
        title: "Password Requirements",
        description: "Please ensure your password meets all requirements.",
        variant: "destructive",
      });
      return;
    }
    if (!passwordsMatch) {
      toast({
        title: "Passwords Don't Match",
        description: "Please ensure both passwords are the same.",
        variant: "destructive",
      });
      return;
    }
    setPasswordMutation.mutate();
  };

  const handleAddAddress = () => {
    if (!addressForm.line1 || !addressForm.city || !addressForm.region || !addressForm.postalCode) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required address fields",
        variant: "destructive",
      });
      return;
    }
    if (!addressForm.hasLoadingDock && !addressForm.hasForklift && !addressForm.requiresLiftgate) {
      toast({
        title: "Validation Error",
        description: "Please select at least one delivery option (liftgate, loading dock, or forklift)",
        variant: "destructive",
      });
      return;
    }
    addAddressMutation.mutate();
  };

  if (!activeToken) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Invalid Link
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              This setup link appears to be invalid. Please check your email for the correct link or contact support.
            </p>
            <Button onClick={() => navigate("/")}>Go to Homepage</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 flex flex-col items-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Validating your setup link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !setupData?.valid) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Setup Link Issue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              {(error as Error)?.message || "This setup link is invalid or has expired. Please contact support for a new link."}
            </p>
            <Button onClick={() => navigate("/")}>Go to Homepage</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (setupData.setupStatus.isComplete && setupData.account?.isActive) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="h-5 w-5" />
              Account Already Active
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Your account is already set up and activated. You can log in to start shopping.
            </p>
            <Button onClick={() => navigate("/login")}>Go to Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { setupStatus } = setupData;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Account Setup</h1>
          <p className="text-muted-foreground">
            Welcome{setupData.user.firstName ? `, ${setupData.user.firstName}` : ""}! Let's finish setting up your account for {setupData.account?.companyName || "your business"}.
          </p>
        </div>

        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {setupStatus.hasPassword ? (
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              ) : (
                <Circle className={`h-6 w-6 ${currentStep === "password" ? "text-primary" : "text-muted-foreground"}`} />
              )}
              <span className={`font-medium ${setupStatus.hasPassword ? "text-green-600" : currentStep === "password" ? "text-primary" : "text-muted-foreground"}`}>
                Set Password
              </span>
            </div>
            <div className="w-12 h-0.5 bg-gray-200" />
            <div className="flex items-center gap-2">
              {setupStatus.hasDefaultShippingAddress ? (
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              ) : (
                <Circle className={`h-6 w-6 ${currentStep === "address" ? "text-primary" : "text-muted-foreground"}`} />
              )}
              <span className={`font-medium ${setupStatus.hasDefaultShippingAddress ? "text-green-600" : currentStep === "address" ? "text-primary" : "text-muted-foreground"}`}>
                Add Shipping Address
              </span>
            </div>
          </div>
        </div>

        {currentStep === "password" && !setupStatus.hasPassword && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Create Your Password
              </CardTitle>
              <CardDescription>
                Choose a secure password for your account. You'll use this along with your email ({setupData.user.email}) to log in.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    data-testid="input-setup-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="button-toggle-password-visibility"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="bg-muted rounded-lg p-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Password Requirements:</p>
                {passwordRequirements.map((req, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    {req.met ? (
                      <Check className="h-4 w-4 text-green-600" />
                    ) : (
                      <X className="h-4 w-4 text-muted-foreground" />
                    )}
                    <span className={req.met ? 'text-green-600' : 'text-muted-foreground'}>
                      {req.text}
                    </span>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    data-testid="input-setup-confirm-password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    data-testid="button-toggle-confirm-password-visibility"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirmPassword && (
                  <div className="flex items-center gap-2 text-sm">
                    {passwordsMatch ? (
                      <>
                        <Check className="h-4 w-4 text-green-600" />
                        <span className="text-green-600">Passwords match</span>
                      </>
                    ) : (
                      <>
                        <X className="h-4 w-4 text-red-500" />
                        <span className="text-red-500">Passwords don't match</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              <Button 
                onClick={handleSetPassword} 
                className="w-full"
                disabled={setPasswordMutation.isPending || !allRequirementsMet || !passwordsMatch}
                data-testid="button-set-password"
              >
                {setPasswordMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Set Password & Continue
              </Button>
            </CardContent>
          </Card>
        )}

        {(currentStep === "address" || setupStatus.hasPassword) && setupData.defaultShippingAddress && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                Default Shipping Address Saved
              </CardTitle>
              <CardDescription>
                Your default shipping address was saved during registration. You can proceed to activate your account.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="font-medium">
                  {setupData.defaultShippingAddress.locationName || "Default Address"}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {setupData.defaultShippingAddress.line1}
                  {setupData.defaultShippingAddress.line2 && `, ${setupData.defaultShippingAddress.line2}`}
                </div>
                <div className="text-sm text-muted-foreground">
                  {setupData.defaultShippingAddress.city}, {setupData.defaultShippingAddress.region} {setupData.defaultShippingAddress.postalCode}
                </div>
                <div className="text-xs text-muted-foreground mt-2">
                  {setupData.defaultShippingAddress.requiresLiftgate && "• Liftgate required"}
                  {setupData.defaultShippingAddress.hasLoadingDock && "• Has loading dock"}
                  {setupData.defaultShippingAddress.hasForklift && "• Has forklift"}
                </div>
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={() => completeSetupMutation.mutate()}
                  className="flex-1"
                  disabled={completeSetupMutation.isPending}
                  data-testid="button-activate-account"
                >
                  {completeSetupMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Activate My Account
                </Button>
              </div>
              
              <p className="text-sm text-center text-muted-foreground">
                You can add more shipping addresses after logging in.
              </p>
            </CardContent>
          </Card>
        )}

        {(currentStep === "address" || setupStatus.hasPassword) && !setupData.defaultShippingAddress && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5" />
                Add Your Default Shipping Address
              </CardTitle>
              <CardDescription>
                This will be your primary address for freight deliveries. You can add more addresses later.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="locationName">Location Name (Optional)</Label>
                <Input
                  id="locationName"
                  value={addressForm.locationName}
                  onChange={(e) => setAddressForm(prev => ({ ...prev, locationName: e.target.value }))}
                  placeholder="e.g., Main Warehouse, Distribution Center"
                  data-testid="input-location-name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="line1">Street Address *</Label>
                <Input
                  id="line1"
                  value={addressForm.line1}
                  onChange={(e) => setAddressForm(prev => ({ ...prev, line1: e.target.value }))}
                  placeholder="123 Main St"
                  data-testid="input-address-line1"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="line2">Suite / Unit (Optional)</Label>
                <Input
                  id="line2"
                  value={addressForm.line2}
                  onChange={(e) => setAddressForm(prev => ({ ...prev, line2: e.target.value }))}
                  placeholder="Suite 100"
                  data-testid="input-address-line2"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={addressForm.city}
                    onChange={(e) => setAddressForm(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="Minneapolis"
                    data-testid="input-city"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="region">State *</Label>
                  <Select
                    value={addressForm.region}
                    onValueChange={(value) => setAddressForm(prev => ({ ...prev, region: value }))}
                  >
                    <SelectTrigger data-testid="select-region">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map((state) => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postalCode">ZIP Code *</Label>
                  <Input
                    id="postalCode"
                    value={addressForm.postalCode}
                    onChange={(e) => setAddressForm(prev => ({ ...prev, postalCode: e.target.value }))}
                    placeholder="55401"
                    data-testid="input-postal-code"
                  />
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3">Delivery Contact (Optional)</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="deliveryContactName">Contact Name</Label>
                    <Input
                      id="deliveryContactName"
                      value={addressForm.deliveryContactName}
                      onChange={(e) => setAddressForm(prev => ({ ...prev, deliveryContactName: e.target.value }))}
                      placeholder="John Doe"
                      data-testid="input-contact-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deliveryContactPhone">Contact Phone</Label>
                    <Input
                      id="deliveryContactPhone"
                      value={addressForm.deliveryContactPhone}
                      onChange={(e) => setAddressForm(prev => ({ ...prev, deliveryContactPhone: e.target.value }))}
                      placeholder="(555) 123-4567"
                      data-testid="input-contact-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="deliveryContactEmail">Contact Email</Label>
                    <Input
                      id="deliveryContactEmail"
                      value={addressForm.deliveryContactEmail}
                      onChange={(e) => setAddressForm(prev => ({ ...prev, deliveryContactEmail: e.target.value }))}
                      placeholder="contact@company.com"
                      data-testid="input-contact-email"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-3">Delivery Equipment Options *</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Select the option that best describes your delivery location. This helps us provide accurate freight quotes.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="requiresLiftgate"
                      checked={addressForm.requiresLiftgate}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setAddressForm(prev => ({ ...prev, requiresLiftgate: true, hasLoadingDock: false, hasForklift: false }));
                        } else {
                          setAddressForm(prev => ({ ...prev, requiresLiftgate: false }));
                        }
                      }}
                      data-testid="checkbox-liftgate"
                    />
                    <Label htmlFor="requiresLiftgate" className="font-normal">
                      Liftgate required (recommended for locations without loading dock/forklift)
                    </Label>
                  </div>
                  
                  <div className="ml-6 space-y-3 border-l-2 pl-4">
                    <p className="text-sm text-muted-foreground">Or, if you have dock/forklift equipment:</p>
                    
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="hasLoadingDock"
                        checked={addressForm.hasLoadingDock}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setAddressForm(prev => ({ ...prev, hasLoadingDock: true, requiresLiftgate: false }));
                          } else {
                            setAddressForm(prev => ({ ...prev, hasLoadingDock: false }));
                          }
                        }}
                        data-testid="checkbox-loading-dock"
                      />
                      <Label htmlFor="hasLoadingDock" className="font-normal">
                        This address has a loading dock
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="hasForklift"
                        checked={addressForm.hasForklift}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setAddressForm(prev => ({ ...prev, hasForklift: true, requiresLiftgate: false }));
                          } else {
                            setAddressForm(prev => ({ ...prev, hasForklift: false }));
                          }
                        }}
                        data-testid="checkbox-forklift"
                      />
                      <Label htmlFor="hasForklift" className="font-normal">
                        This address has forklift equipment
                      </Label>
                    </div>
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleAddAddress} 
                className="w-full"
                disabled={addAddressMutation.isPending || completeSetupMutation.isPending}
                data-testid="button-add-address"
              >
                {(addAddressMutation.isPending || completeSetupMutation.isPending) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Add Address & Activate Account
              </Button>
            </CardContent>
          </Card>
        )}

        {setupStatus.hasPassword && setupStatus.hasDefaultShippingAddress && (
          <Card>
            <CardContent className="py-8 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Setup Complete!</h3>
              <p className="text-muted-foreground mb-4">
                Your account is being activated. You'll be redirected to the login page shortly.
              </p>
              <Button onClick={() => navigate("/login")} data-testid="button-go-to-login">
                Go to Login
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
